// src/components/TikTokShopSection.jsx
// TikTok Shop revenue tracking block. Lives inside the TikTok Ads page (so
// admins find shop revenue near related ad data) but is a self-contained
// component so the parent page stays readable.
//
// MVP — manual entry only. The schema (tiktok_shops + tiktok_shop_daily)
// also supports source='api' for when we wire up TikTok Shop Partner API.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { authFetch } from '../lib/authFetch';
import { useAuth } from '../contexts/AuthContext';
import {
  Card, CardContent, Button, Input, Badge, Modal, ModalFooter,
} from './ui';
import {
  ShoppingBag, Plus, Trash2, RefreshCw, TrendingUp,
  Plug, Unplug, ExternalLink, Check, LogIn, Bug, ChevronDown, ChevronRight,
  Bell, Loader2,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { formatCurrency } from '../lib/utils';
import toast from 'react-hot-toast';

const REGIONS = ['US', 'UK', 'ID', 'TH', 'VN', 'PH', 'MY', 'SG'];

export default function TikTokShopSection() {
  const { user } = useAuth();

  const [shops, setShops]         = useState([]);
  const [daily, setDaily]         = useState([]);
  const [selectedShop, setSel]    = useState(null);
  const [loading, setLoading]     = useState(true);

  // Modals
  const [showAddShop, setShowAddShop]     = useState(false);
  const [showAddEntry, setShowAddEntry]   = useState(false);
  const [showConnectApi, setShowConnectApi] = useState(false);
  const [showShopPicker, setShowShopPicker] = useState(false);
  const [pickerShops, setPickerShops]       = useState([]);
  const [pickerTokens, setPickerTokens]     = useState(null);
  const [fetching, setFetching]           = useState(false);
  const [connecting, setConnecting]       = useState(false);
  const [shopForm, setShopForm] = useState({ shop_name: '', region: 'US', currency: 'USD' });
  const [entryForm, setEntryForm] = useState({
    date: new Date().toISOString().split('T')[0],
    gmv: '', orders: '', refunds: '', fees: '',
  });
  const [apiForm, setApiForm] = useState({
    app_key: '', app_secret: '', access_token: '', shop_cipher: '',
  });

  // Promotion reminder settings (per shop; columns from the
  // 2026-07-17-tiktok-shop-promo-reminders migration).
  const [reminderForm, setReminderForm] = useState(null);
  const [savingReminders, setSavingReminders] = useState(false);
  useEffect(() => {
    if (!selectedShop) { setReminderForm(null); return; }
    setReminderForm({
      enabled: !!selectedShop.promo_reminders_enabled,
      days: selectedShop.promo_reminder_days || 2,
      email: selectedShop.promo_reminder_email || '',
    });
  }, [selectedShop]);

  const saveReminders = async () => {
    if (!selectedShop || !reminderForm) return;
    setSavingReminders(true);
    try {
      const updates = {
        promo_reminders_enabled: reminderForm.enabled,
        promo_reminder_days: parseInt(reminderForm.days) || 2,
        promo_reminder_email: reminderForm.email.trim() || null,
      };
      const { error } = await supabase
        .from('tiktok_shops').update(updates).eq('id', selectedShop.id);
      if (error) {
        if (/column .* does not exist/i.test(error.message || '')) {
          toast.error('Run the 2026-07-17-tiktok-shop-promo-reminders migration first');
        } else {
          toast.error(error.message);
        }
        return;
      }
      setShops(prev => prev.map(s => (s.id === selectedShop.id ? { ...s, ...updates } : s)));
      setSel(prev => (prev ? { ...prev, ...updates } : prev));
      toast.success(reminderForm.enabled ? 'Promotion reminders on' : 'Promotion reminders off');
    } finally {
      setSavingReminders(false);
    }
  };

  // Debug panel — captures the `logs` array from every TikTok Shop call.
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugOpen, setDebugOpen] = useState(false);
  const appendLogs = (label, logs) => {
    if (!Array.isArray(logs) || logs.length === 0) return;
    const stamped = logs.map(l => `[${new Date().toLocaleTimeString()}] ${label} — ${l}`);
    setDebugLogs(prev => [...stamped, ...prev].slice(0, 200));
  };

  // OAuth return: when TikTok redirects back to /tiktok-shop?code=...&state=...
  // we exchange the code, then pop the shop picker so the user picks which
  // shop to bind (most apps return one).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code || !user) return;
    if (state && !state.startsWith(`${user.id}:`)) {
      // State mismatch — not our flow.
      return;
    }
    (async () => {
      const tid = toast.loading('Completing TikTok Shop authorisation…');
      try {
        const res = await authFetch('/api/fetch-tiktok-shop', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'callback', authCode: code, debug: true }),
        });
        const data = await res.json();
        appendLogs('callback', data.logs);
        if (!data.success) { toast.error(data.error || 'Authorisation failed', { id: tid }); return; }
        if ((data.shops || []).length === 0) {
          toast.error('No shops returned by TikTok', { id: tid });
          return;
        }
        setPickerShops(data.shops);
        setPickerTokens({
          access_token:  data.access_token,
          refresh_token: data.refresh_token,
          expires_in:    data.expires_in,
          app_key:       data.app_key,
        });
        setShowShopPicker(true);
        toast.success('Pick a shop to connect', { id: tid });
      } catch (e) {
        toast.error(e.message, { id: tid });
      } finally {
        // Strip OAuth params from URL so refresh doesn't re-trigger.
        const next = new URLSearchParams(searchParams);
        next.delete('code'); next.delete('state'); next.delete('shop_cipher'); next.delete('app_key');
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: shopRows, error: shopErr } = await supabase
        .from('tiktok_shops')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (shopErr) {
        // Table doesn't exist yet → migration hasn't run. Render nothing.
        if (/relation .* does not exist/i.test(shopErr.message || '')) {
          setShops([]); setLoading(false); return;
        }
        throw shopErr;
      }

      const safeShops = shopRows || [];
      setShops(safeShops);

      const activeId = selectedShop?.id || safeShops[0]?.id || null;
      if (activeId) {
        const since = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
        const { data: dailyRows } = await supabase
          .from('tiktok_shop_daily')
          .select('*')
          .eq('user_id', user.id)
          .eq('shop_id', activeId)
          .gte('date', since)
          .order('date', { ascending: false });
        setDaily(dailyRows || []);
        if (!selectedShop) setSel(safeShops.find(s => s.id === activeId) || null);
      } else {
        setDaily([]);
      }
    } catch (e) {
      console.warn('[TikTokShop] load failed:', e.message);
    } finally {
      setLoading(false);
    }
  }, [user, selectedShop]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Add shop ─────────────────────────────────────────────────────────────
  const saveShop = async () => {
    if (!shopForm.shop_name.trim()) return toast.error('Shop name required');
    const { data, error } = await supabase
      .from('tiktok_shops')
      .insert({
        user_id: user.id,
        shop_name: shopForm.shop_name.trim(),
        region:   shopForm.region,
        currency: shopForm.currency,
        is_connected: false,
      })
      .select().single();
    if (error) return toast.error(error.message);
    setShops(s => [...s, data]);
    setSel(data);
    setShopForm({ shop_name: '', region: 'US', currency: 'USD' });
    setShowAddShop(false);
    toast.success('Shop added');
  };

  const removeShop = async (shop) => {
    if (!window.confirm(`Remove "${shop.shop_name}" and all its revenue rows?`)) return;
    const { error } = await supabase.from('tiktok_shops').delete().eq('id', shop.id);
    if (error) return toast.error(error.message);
    setShops(s => s.filter(x => x.id !== shop.id));
    if (selectedShop?.id === shop.id) setSel(null);
    toast.success('Shop removed');
  };

  // ── Add daily revenue ────────────────────────────────────────────────────
  const saveEntry = async () => {
    if (!selectedShop) return toast.error('Select a shop first');
    const gmv     = parseFloat(entryForm.gmv)     || 0;
    const orders  = parseInt(entryForm.orders)    || 0;
    const refunds = parseFloat(entryForm.refunds) || 0;
    const fees    = parseFloat(entryForm.fees)    || 0;
    const net     = +(gmv - refunds - fees).toFixed(2);

    const { error } = await supabase.from('tiktok_shop_daily').upsert({
      user_id: user.id,
      shop_id: selectedShop.id,
      date:    entryForm.date,
      gmv, orders, refunds, fees,
      net_revenue: net,
      source: 'manual',
    }, { onConflict: 'user_id,shop_id,date' });
    if (error) return toast.error(error.message);

    setEntryForm({ date: new Date().toISOString().split('T')[0], gmv: '', orders: '', refunds: '', fees: '' });
    setShowAddEntry(false);
    toast.success('Entry saved');
    loadData();
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Remove revenue for ${entry.date}?`)) return;
    await supabase.from('tiktok_shop_daily').delete().eq('id', entry.id);
    toast.success('Entry removed');
    loadData();
  };

  // ── OAuth flow: kick off ─────────────────────────────────────────────────
  const connectOAuth = async () => {
    setConnecting(true);
    const tid = toast.loading('Building TikTok Shop authorise URL…');
    try {
      const res = await authFetch('/api/fetch-tiktok-shop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getAuthUrl', debug: true }),
      });
      const data = await res.json();
      appendLogs('getAuthUrl', data.logs);
      if (!data.success) { toast.error(data.error || 'Failed', { id: tid }); setConnecting(false); return; }
      toast.dismiss(tid);
      window.location.href = data.authUrl;
    } catch (e) {
      toast.error(e.message, { id: tid });
      setConnecting(false);
    }
  };

  // After OAuth callback, the user picks one of the returned shops.
  const selectShopFromPicker = async (s) => {
    if (!pickerTokens) return;
    const tid = toast.loading('Saving shop…');
    try {
      const res = await authFetch('/api/fetch-tiktok-shop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'selectShop',
          shop_id: s.shop_id, shop_name: s.shop_name, shop_cipher: s.shop_cipher,
          region: s.region, currency: s.currency,
          ...pickerTokens,
          debug: true,
        }),
      });
      const data = await res.json();
      appendLogs('selectShop', data.logs);
      if (!data.success) { toast.error(data.error || 'Failed', { id: tid }); return; }
      toast.success(`Connected ${data.shop.shop_name}`, { id: tid });
      setShowShopPicker(false);
      setPickerShops([]); setPickerTokens(null);
      setSel(data.shop);
      loadData();
    } catch (e) {
      toast.error(e.message, { id: tid });
    }
  };

  // ── Manual cred fallback (kept for admins testing without OAuth) ─────────
  const connectApi = async () => {
    if (!selectedShop) return;
    const { app_key, app_secret, access_token, shop_cipher } = apiForm;
    if (!app_key || !app_secret || !access_token || !shop_cipher) {
      return toast.error('All four fields are required');
    }
    const tid = toast.loading('Saving credentials…');
    const res = await authFetch('/api/fetch-tiktok-shop', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'connect_api', shop_id: selectedShop.id, debug: true, ...apiForm }),
    });
    const data = await res.json();
    appendLogs('connect_api', data.logs);
    if (data.success) {
      toast.success('API connected', { id: tid });
      setApiForm({ app_key: '', app_secret: '', access_token: '', shop_cipher: '' });
      setShowConnectApi(false);
      loadData();
    } else {
      toast.error(data.error || 'Failed to connect', { id: tid });
    }
  };

  const fetchOrders = async () => {
    if (!selectedShop?.is_connected) return toast.error('Connect the API first');
    setFetching(true);
    const tid = toast.loading('Pulling orders from TikTok Shop…');
    try {
      const res = await authFetch('/api/fetch-tiktok-shop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'fetch', shop_id: selectedShop.id, days: 30, debug: true }),
      });
      const data = await res.json();
      appendLogs('fetch', data.logs);
      if (data.success) {
        toast.success(`Pulled ${data.daysWritten} day${data.daysWritten === 1 ? '' : 's'} of revenue`, { id: tid });
        loadData();
      } else {
        toast.error(data.error || 'Fetch failed', { id: tid });
        if (data.logs?.length) setDebugOpen(true);   // surface errors automatically
      }
    } catch (e) {
      toast.error(e.message, { id: tid });
    }
    setFetching(false);
  };

  const disconnectApi = async () => {
    if (!selectedShop) return;
    if (!window.confirm('Disconnect API? Historical daily revenue is kept; manual entry stays available.')) return;
    const res = await authFetch('/api/fetch-tiktok-shop', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'disconnect', shop_id: selectedShop.id, debug: true }),
    });
    const data = await res.json();
    appendLogs('disconnect', data.logs);
    if (data.success) {
      toast.success('Disconnected');
      loadData();
    } else {
      toast.error(data.error || 'Failed');
    }
  };

  // ── Aggregates for the summary row ───────────────────────────────────────
  const summary = useMemo(() => {
    const today    = new Date().toISOString().split('T')[0];
    const yest     = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    let monthGmv = 0, monthOrders = 0, monthNet = 0;
    const todayRow = daily.find(d => d.date === today);
    const yestRow  = daily.find(d => d.date === yest);
    for (const d of daily) {
      monthGmv    += parseFloat(d.gmv) || 0;
      monthOrders += parseInt(d.orders) || 0;
      monthNet    += parseFloat(d.net_revenue) || 0;
    }
    return { todayRow, yestRow, monthGmv, monthOrders, monthNet };
  }, [daily]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-rose-500" /> TikTok Shop
          {selectedShop?.is_connected
            ? <Badge className="text-xs font-normal bg-emerald-500">API connected</Badge>
            : <Badge variant="outline" className="text-xs font-normal">manual entry</Badge>}
        </h2>
        <div className="flex flex-wrap gap-2">
          {selectedShop?.is_connected && (
            <Button variant="outline" size="sm" onClick={fetchOrders} disabled={fetching}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${fetching ? 'animate-spin' : ''}`} />
              {fetching ? 'Fetching…' : 'Fetch orders'}
            </Button>
          )}
          {selectedShop && !selectedShop.is_connected && (
            <>
              <Button size="sm" onClick={connectOAuth} disabled={connecting}>
                <LogIn className="h-4 w-4 mr-1.5" />Connect via TikTok
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowConnectApi(true)} title="Manual credential entry (admin fallback)">
                <Plug className="h-4 w-4 mr-1.5" />Manual
              </Button>
            </>
          )}
          {!selectedShop && (
            <Button size="sm" onClick={connectOAuth} disabled={connecting}>
              <LogIn className="h-4 w-4 mr-1.5" />Connect via TikTok
            </Button>
          )}
          {selectedShop?.is_connected && (
            <Button variant="outline" size="sm" onClick={disconnectApi}>
              <Unplug className="h-4 w-4 mr-1.5" />Disconnect
            </Button>
          )}
          {selectedShop && (
            <Button variant="outline" size="sm" onClick={() => setShowAddEntry(true)}>
              <Plus className="h-4 w-4 mr-1.5" />Add revenue
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowAddShop(true)}>
            <Plus className="h-4 w-4 mr-1.5" />Add shop
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="h-32 bg-card rounded-2xl border border-border animate-pulse" />
      ) : shops.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <ShoppingBag className="h-10 w-10 text-muted-foreground/60 mx-auto mb-3" />
            <p className="font-medium text-foreground mb-1">No TikTok Shop connected yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Add a shop and log daily GMV. Partner API integration coming later — for now, use manual entry.
            </p>
            <Button onClick={() => setShowAddShop(true)}><Plus className="h-4 w-4 mr-1.5" />Add shop</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Shop selector */}
          <div className="flex flex-wrap gap-2">
            {shops.map(s => (
              <button
                key={s.id}
                onClick={() => setSel(s)}
                className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                  selectedShop?.id === s.id
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                {s.shop_name} <span className="opacity-60 ml-1">· {s.region}</span>
              </button>
            ))}
          </div>

          {selectedShop && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryCard label="Today GMV"      value={formatCurrency(summary.todayRow?.gmv || 0)} sub={`${summary.todayRow?.orders || 0} orders`} />
                <SummaryCard label="Yesterday GMV"  value={formatCurrency(summary.yestRow?.gmv || 0)}  sub={`${summary.yestRow?.orders || 0} orders`} />
                <SummaryCard label="30-day GMV"     value={formatCurrency(summary.monthGmv)}           sub={`${summary.monthOrders} orders`} />
                <SummaryCard label="30-day Net"     value={formatCurrency(summary.monthNet)}           sub="after refunds + fees" icon={TrendingUp} />
              </div>

              {/* Promotion reminders — API-connected shops only (the daily
                  cron reads promotions via the Partner API). */}
              {selectedShop.is_connected && reminderForm && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3 flex-wrap">
                      <div className="flex-1 min-w-[220px]">
                        <p className="font-medium text-sm flex items-center gap-1.5">
                          <Bell className="h-3.5 w-3.5 text-rose-500" /> Promotion reminders
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Emails you before a promo ends so you can duplicate or create the next one —
                          plus a weekly nudge if nothing is running or scheduled.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={reminderForm.enabled}
                            onChange={e => setReminderForm(f => ({ ...f, enabled: e.target.checked }))}
                            className="h-4 w-4 accent-rose-500"
                          />
                          Enabled
                        </label>
                        <select
                          value={reminderForm.days}
                          onChange={e => setReminderForm(f => ({ ...f, days: e.target.value }))}
                          disabled={!reminderForm.enabled}
                          className="text-sm bg-background border border-border rounded-lg px-2 py-1.5 disabled:opacity-50"
                          title="How long before a promo ends to remind you"
                        >
                          <option value="1">1 day before</option>
                          <option value="2">2 days before</option>
                          <option value="3">3 days before</option>
                          <option value="7">7 days before</option>
                        </select>
                        <Input
                          type="email"
                          value={reminderForm.email}
                          onChange={e => setReminderForm(f => ({ ...f, email: e.target.value }))}
                          disabled={!reminderForm.enabled}
                          placeholder={user?.email || 'Reminder email'}
                          className="w-52 h-9 text-sm"
                        />
                        <Button size="sm" onClick={saveReminders} disabled={savingReminders}>
                          {savingReminders && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                          Save
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Daily table */}
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                    <p className="text-sm font-medium">Daily revenue · last 30 days</p>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={loadData}>
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => removeShop(selectedShop)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30">
                        <tr>{['Date', 'GMV', 'Orders', 'Refunds', 'Fees', 'Net', ''].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-medium text-muted-foreground">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {daily.length === 0 ? (
                          <tr><td colSpan={7} className="text-center text-muted-foreground py-10">No revenue logged yet for this shop.</td></tr>
                        ) : daily.map(d => (
                          <tr key={d.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2 font-medium">{d.date}</td>
                            <td className="px-3 py-2">{formatCurrency(d.gmv)}</td>
                            <td className="px-3 py-2">{d.orders}</td>
                            <td className="px-3 py-2 text-red-500">{d.refunds > 0 ? `(${formatCurrency(d.refunds)})` : '—'}</td>
                            <td className="px-3 py-2 text-red-500">{d.fees > 0 ? `(${formatCurrency(d.fees)})` : '—'}</td>
                            <td className="px-3 py-2 font-semibold text-emerald-600">{formatCurrency(d.net_revenue)}</td>
                            <td className="px-3 py-2 text-right">
                              <button className="p-1 hover:bg-muted rounded" onClick={() => removeEntry(d)} title="Remove">
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* ── Add shop modal ──────────────────────────────────────────────── */}
      <Modal isOpen={showAddShop} onClose={() => setShowAddShop(false)} title="Add TikTok Shop">
        <div className="space-y-3">
          <Input
            placeholder="Shop name"
            value={shopForm.shop_name}
            onChange={e => setShopForm({ ...shopForm, shop_name: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Region</p>
              <select value={shopForm.region} onChange={e => setShopForm({ ...shopForm, region: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground">
                {REGIONS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Currency</p>
              <Input value={shopForm.currency} onChange={e => setShopForm({ ...shopForm, currency: e.target.value.toUpperCase().slice(0,3) })} />
            </div>
          </div>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowAddShop(false)}>Cancel</Button>
            <Button onClick={saveShop}>Add</Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* ── Connect API modal ──────────────────────────────────────────── */}
      <Modal isOpen={showConnectApi} onClose={() => setShowConnectApi(false)} title="Connect TikTok Shop API" size="md">
        <div className="space-y-3 text-sm">
          <div className="bg-muted/40 border border-border rounded-lg p-3 text-xs space-y-1.5">
            <p className="font-medium text-foreground">Where to get these:</p>
            <ol className="list-decimal pl-4 text-muted-foreground space-y-0.5">
              <li>Open <a href="https://partner.tiktokshop.com" target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-0.5">TikTok Shop Partner Center<ExternalLink className="h-3 w-3" /></a> and create a developer app.</li>
              <li>The app page lists the <strong>App key</strong> and <strong>App secret</strong>.</li>
              <li>Authorise the app on your shop — you'll get an <strong>access token</strong> and a <strong>shop cipher</strong>.</li>
            </ol>
          </div>

          {[
            { key: 'app_key',      label: 'App key',      placeholder: 'tt_app_xxxxxxxxxxxxxxxx' },
            { key: 'app_secret',   label: 'App secret',   placeholder: '32+ char secret',         type: 'password' },
            { key: 'access_token', label: 'Access token', placeholder: 'TTP_xxxxxxxxxxxxxxxx',    type: 'password' },
            { key: 'shop_cipher',  label: 'Shop cipher',  placeholder: 'GCP_xxxxxxxxxxxxxxxxxxxx' },
          ].map(f => (
            <div key={f.key}>
              <label className="text-xs font-medium text-muted-foreground">{f.label}</label>
              <Input
                type={f.type || 'text'}
                value={apiForm[f.key]}
                onChange={e => setApiForm({ ...apiForm, [f.key]: e.target.value.trim() })}
                placeholder={f.placeholder}
                className="mt-1"
              />
            </div>
          ))}

          <ModalFooter>
            <Button variant="outline" onClick={() => setShowConnectApi(false)}>Cancel</Button>
            <Button onClick={connectApi}><Check className="h-4 w-4 mr-1.5" />Save & connect</Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* ── Add daily entry modal ───────────────────────────────────────── */}
      <Modal isOpen={showAddEntry} onClose={() => setShowAddEntry(false)} title="Log daily revenue">
        <div className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Date</p>
            <Input type="date" value={entryForm.date} onChange={e => setEntryForm({ ...entryForm, date: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">GMV ({selectedShop?.currency || '$'})</p>
              <Input type="number" step="0.01" value={entryForm.gmv} onChange={e => setEntryForm({ ...entryForm, gmv: e.target.value })} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Orders</p>
              <Input type="number" value={entryForm.orders} onChange={e => setEntryForm({ ...entryForm, orders: e.target.value })} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Refunds</p>
              <Input type="number" step="0.01" value={entryForm.refunds} onChange={e => setEntryForm({ ...entryForm, refunds: e.target.value })} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Fees</p>
              <Input type="number" step="0.01" value={entryForm.fees} onChange={e => setEntryForm({ ...entryForm, fees: e.target.value })} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded">
            Net revenue = GMV − refunds − fees, calculated automatically when you save.
          </p>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowAddEntry(false)}>Cancel</Button>
            <Button onClick={saveEntry}>Save</Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* ── Shop picker (post-OAuth) ────────────────────────────────────── */}
      <Modal isOpen={showShopPicker} onClose={() => setShowShopPicker(false)} title="Pick a shop to connect">
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {pickerShops.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No shops authorised this app.</p>
          ) : pickerShops.map(s => (
            <div key={s.shop_cipher} className="flex items-center justify-between p-3 border border-border rounded-lg hover:border-primary/50">
              <div>
                <p className="font-medium text-sm">{s.shop_name}</p>
                <p className="text-xs text-muted-foreground">{s.region || '—'} · {s.currency || '—'} · {s.shop_id || s.shop_cipher.slice(0, 12) + '…'}</p>
              </div>
              <Button size="sm" onClick={() => selectShopFromPicker(s)}>
                <Check className="h-3.5 w-3.5 mr-1" />Connect
              </Button>
            </div>
          ))}
        </div>
      </Modal>

      {/* ── Debug panel ─────────────────────────────────────────────────── */}
      {debugLogs.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => setDebugOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/40"
          >
            <span className="flex items-center gap-1.5">
              <Bug className="h-3.5 w-3.5" />
              Debug log ({debugLogs.length} entr{debugLogs.length === 1 ? 'y' : 'ies'})
            </span>
            {debugOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          {debugOpen && (
            <div className="px-3 pb-3 pt-1 max-h-72 overflow-y-auto font-mono text-[11px] leading-relaxed space-y-0.5">
              {debugLogs.map((line, i) => (
                <div
                  key={i}
                  className={line.toLowerCase().includes('error') || line.toLowerCase().includes('fatal')
                    ? 'text-red-500'
                    : 'text-muted-foreground'}
                >
                  {line}
                </div>
              ))}
              <button onClick={() => setDebugLogs([])} className="text-xs text-primary hover:underline mt-2">Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-card rounded-xl border border-border p-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        {Icon && <Icon className="h-3.5 w-3.5 text-emerald-500" />}
      </div>
      <p className="text-lg font-bold text-foreground">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
